import React, { useEffect, useState } from "react";
import { CouponForm } from "../Forms/CouponForm";
import { useNavigate } from "react-router-dom";
import { debounce } from "../../../helper/Debounce";
import PaginationButtons from "../../../components/Common/Pagination";
import TableInfoBar from "../../../components/Admin/TableInfoBar";
import { Toast } from "../../../components/Common/Toast";
import Button from "../../../components/Common/Button";
import SearchInput from "../../../components/Common/SearchInput";
import Dialogs from "../../../components/Common/Dialog";
import { CouponModel } from "../../../interfaces/coupon.interface";
import { deleteCoupon, getCoupons, searchCoupons } from "./services/AdminCoupon.service";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import CouponTable from "./components/CouponsTable";
import { TokenCookieManager } from "../../../utils/tokenCookies";

const Coupons = () => {
    const [openDialog, setOpenDialog] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [coupons, setCoupons] = useState<CouponModel[]>([]);
    const [couponId, setCouponId] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [couponDetails, setCouponDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    const navigate = useNavigate();

    useEffect(() => {
        if (!TokenCookieManager.hasToken()) {
            navigate("/admin");
        }
    });

    const handleSearchCoupons = async () => {
        try {
            setLoading(true);
            const data = await searchCoupons(searchTerm);
            setCoupons(data);
        } catch (error) { } finally {
            setLoading(false);
        }
    };

    const debouncedSearchCoupons = debounce(handleSearchCoupons, 300);

    useEffect(() => {
        if (searchTerm.length >= 3) {
            debouncedSearchCoupons();
        }
        if (!searchTerm) {
            fetchCoupons();
        }
    }, [searchTerm]);


    const handlePageChange = (page: number | any) => {
        if (page > 0 && page <= totalPages!) setCurrentPage(+page);
    };

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const data = await getCoupons(currentPage);
            setCoupons(data.coupons);
            setTotalPages(data.totalPages);
        } catch (error) { } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!searchTerm) {
            fetchCoupons();
        }
    }, [searchTerm]);

    useEffect(() => {
        fetchCoupons();
    }, [currentPage]);

    const handleDelete = async () => {
        try {
            if (couponId) {
                await deleteCoupon(couponId);
                Toast("Coupon Deleted Successfully");
                fetchCoupons();
                setOpenDialog(false);
            }
        } catch (error) {
            Toast("Coupon Delete Failed", true);
        }
    };

    const handleClose = () => {
        setOpenDialog(false);
    };

    return (
        <div className="p-0 md:p-4">
            {showForm ? (
                <CouponForm setShowForm={setShowForm} getCoupons={fetchCoupons} couponDetails={couponDetails} />
            ) : (
                <>
                    <div>
                        <div className="flex justify-between items-baseline gap-2 flex-wrap my-5">
                            <Button
                                title="New Coupon"
                                className="w-40 flex justify-center gap-2 bg-storyblue text-white font-bold rounded-md text-base p-[0.4rem]"
                                onClick={() => {
                                    setShowForm(true);
                                    setCouponDetails(null);
                                }}
                            />
                            <SearchInput placeholder={"Search Coupon"} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                    </div>
                    {loading ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            <CouponTable
                                coupons={coupons}
                                setOpenDialog={setOpenDialog}
                                setCouponId={setCouponId}
                            />
                            <div className="flex justify-between items-center mt-4">
                                <TableInfoBar
                                    currentPage={currentPage}
                                    pageSize={25}
                                    totalPages={totalPages || 0}
                                    totalRecords={undefined}
                                    recordsOnPage={coupons.length}
                                />
                                {(totalPages ?? 0) > 1 && (
                                    <PaginationButtons
                                        totalPages={totalPages!}
                                        currentPage={currentPage}
                                        handlePageChange={handlePageChange}
                                    />
                                )}
                            </div>
                        </>
                    )}
                    <Dialogs openDialog={openDialog} handelClose={handleClose} handelProcess={handleDelete} />
                </>
            )}
        </div>
    );
};
export default Coupons;
